import BaseLayer from "ol/layer/Base"
import { GeoserverLayer } from "../state"
import TileLayer from "ol/layer/Tile"
import TileWMS from "ol/source/TileWMS"
import { memoize } from "lodash"
import { getColorStops } from "./model_output"
import distinctColors from "distinct-colors"
import { geoserverCategoricalValues } from "../layers/geoserver_categorical"

const MAX_CATEGORICAL_RASTER_VALUES = 64
const RASTER_RAMP_STEP_COUNT = 60

function toHex(color: [number, number, number, number]): string {
    return `#${color[0].toString(16).padStart(2, '0')}${color[1].toString(16).padStart(2, '0')}${color[2].toString(16).padStart(2, '0')}`
}

function getCategoricalRasterColors(count: number): [number, number, number, number][] {
    return distinctColors({ count: Math.max(count, 1) }).map(color => color.rgba() as [number, number, number, number])
}

function getConfiguredCategoricalRasterColors(layer: GeoserverLayer): [number, number, number, number][] {
    const values = geoserverCategoricalValues(layer)
    const defaultColors = getCategoricalRasterColors(values.length)
    return defaultColors.map((color, index) => layer.valueColors?.[index] || color)
}

function buildCategoricalColorMapEntries(values: number[], palette: [number, number, number, number][]): string {
    const sortedValues = [...values].sort((a, b) => a - b)

    return sortedValues.map((value, index) => {
        const previousValue = sortedValues[index - 1]
        const nextValue = sortedValues[index + 1]
        const lowerBound = previousValue === undefined
            ? value - Math.max(((nextValue ?? value + 1) - value) / 2, 0.5)
            : (previousValue + value) / 2
        const upperBound = nextValue === undefined
            ? value + Math.max((value - (previousValue ?? value - 1)) / 2, 0.5)
            : (value + nextValue) / 2
        const color = toHex(palette[index])

        return [
            `<ColorMapEntry color="${color}" quantity="${lowerBound}" opacity="1"/>`,
            `<ColorMapEntry color="${color}" quantity="${upperBound}" opacity="1"/>`
        ].join("")
    }).join("")
}

function buildRasterSldBody(layer: GeoserverLayer): string | undefined {
    if (!layer.fill) return undefined

    const layerName = layer.layerName
    const sldOpen = `<StyledLayerDescriptor version="1.0.0" xmlns="http://www.opengis.net/sld" xmlns:ogc="http://www.opengis.net/ogc" xmlns:xlink="http://www.w3.org/1999/xlink" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:schemaLocation="http://www.opengis.net/sld http://schemas.opengis.net/sld/1.0.0/StyledLayerDescriptor.xsd"><NamedLayer><Name>${layerName}</Name><UserStyle><FeatureTypeStyle><Rule><RasterSymbolizer>`
    const sldClose = `</RasterSymbolizer></Rule></FeatureTypeStyle></UserStyle></NamedLayer></StyledLayerDescriptor>`

    // Keep categorical rasters categorical in map view, but still bound the SLD size
    // so we do not regress to the proxy rejecting extremely large query strings.
    if (layer.categoricalRaster) {
        const categoricalValues = geoserverCategoricalValues(layer)
        if (categoricalValues.length > 0 && categoricalValues.length <= MAX_CATEGORICAL_RASTER_VALUES) {
            const palette = getConfiguredCategoricalRasterColors(layer)
            const entries = buildCategoricalColorMapEntries(categoricalValues, palette)

            return `${sldOpen}<ColorMap type="ramp">${entries}</ColorMap>${sldClose}`
        }
    }

    const rampFill = layer.categoricalRaster ? "rainbow-soft" : (layer.fill === "heatmap" ? "jet" : (layer.fill === "greyscale" ? "greys" : layer.fill))
    const rampColors = getColorStops(rampFill, RASTER_RAMP_STEP_COUNT)
        .filter(entry => Array.isArray(entry)) as [number, number, number, number][]
    rampColors.reverse()
    const minValue = layer.rasterMinValue ?? 0
    const maxValue = layer.rasterMaxValue ?? 255
    const quantities = Array.from({ length: rampColors.length }, (_, index) =>
        minValue + (((maxValue - minValue) * index) / Math.max(rampColors.length - 1, 1))
    )
    const entries = quantities.map((quantity, index) =>
        `<ColorMapEntry color="${toHex(rampColors[index])}" quantity="${quantity}" opacity="1"/>`
    ).join("")

    return `${sldOpen}<ColorMap type="ramp">${entries}</ColorMap>${sldClose}`
}

const getSourceWMS = memoize((url: string, layerName: string, attribution: string | undefined, sldBody: string | undefined) =>
    new TileWMS({
        url: url,
        params: { 'LAYERS': layerName, 'TILED': true, ...(sldBody ? { SLD_BODY: sldBody } : {}) },
        serverType: 'geoserver',
        imageSmoothing: false,
        crossOrigin: 'anonymous',
        attributions: attribution ? [attribution] : undefined
    }),
    (url: string, layerName: string, attribution: string | undefined, sldBody: string | undefined) =>
        `${url}|${layerName}|${attribution || ''}|${sldBody || ''}`
)

export function reifyGeoserverWMSLayer(layer: GeoserverLayer, existingLayer: BaseLayer | null) {
    const url = layer.externalLink ? layer.externalLink : 'https://landscapes.wearepal.ai/geoserver/wms'
    const source = getSourceWMS(url, layer.layerName, layer.attribution, buildRasterSldBody(layer))

    if (existingLayer instanceof TileLayer && existingLayer.getSource() === source) {
        existingLayer.setOpacity(layer.opacity)
        return existingLayer
    }

    return new TileLayer({
        source,
        opacity: layer.opacity
    })
}
